/**
 * LegacyToggle — csuite / platform-ops only. Toggles between React UI and
 * legacy GLAC iframe. Sets/clears the apex_legacy_mode cookie. The app
 * shell reads this to decide whether to render the React router or an
 * iframe pointing to the old vanilla client.
 *
 * Role gate: visible to csuite and platform-ops only (#442). Everyone
 * else — reps, supervisors, managers, admins, auditors, agency owners —
 * does not see the control at all.
 */

import { useState, useEffect } from 'react';
import { Monitor } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';

const COOKIE_NAME = 'apex_legacy_mode';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
  return match ? (match[1] ?? null) : null;
}

function setCookie(name: string, value: string, days = 1) {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

export function LegacyToggle() {
  const { user } = useAuth();
  const [legacyMode, setLegacyMode] = useState(false);

  useEffect(() => {
    setLegacyMode(getCookie(COOKIE_NAME) === '1');
  }, []);

  // Role gate — csuite + platform-ops only. Everyone else sees nothing.
  const allowed = user?.role?.toLowerCase() === 'csuite' || !!user?.platformOps;
  if (!allowed) return null;

  const toggle = () => {
    if (legacyMode) {
      deleteCookie(COOKIE_NAME);
      setLegacyMode(false);
    } else {
      setCookie(COOKIE_NAME, '1');
      setLegacyMode(true);
    }
    // Reload to switch between React and legacy
    window.location.reload();
  };

  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[0.6875rem] font-medium border transition-colors cursor-pointer ${
        legacyMode
          ? 'bg-amber-500/10 border-amber-500/20 text-amber-600'
          : 'bg-secondary/30 border-border/60 text-muted-foreground hover:text-foreground'
      }`}
      title={legacyMode ? 'Switch to React UI' : 'Switch to legacy GLAC UI'}
    >
      <Monitor className="w-3 h-3 stroke-[1.5]" />
      {legacyMode ? 'Legacy' : 'GLAC'}
    </button>
  );
}
